import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, BracesIcon, ChevronDownIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { ActorMark } from '../ActorMark';
import { LevelBadge } from '../../ui/LevelBadge';
import { ProcessingTag } from '../../ui/ProcessingTag';
import { formatLatency } from '../../../utils/logbook';
import type { ExecutionStatus, LogbookEvent, ReactionKind } from '../../../types/beheer';
const STATUS: Record<ExecutionStatus, {
  label: string;
  tone: string;
}> = {
  ok: {
    label: 'Uitgevoerd',
    tone: 'text-accent'
  },
  wacht: {
    label: 'Wacht op jou',
    tone: 'text-ink'
  },
  gemeld: {
    label: 'Gemeld',
    tone: 'text-ink-soft'
  },
  geweigerd: {
    label: 'Geweigerd door beleid',
    tone: 'text-amber'
  },
  gestopt: {
    label: 'Gestopt',
    tone: 'text-alarm'
  }
};
const REACTION: Record<ReactionKind, string> = {
  goedgekeurd: 'Goedgekeurd',
  afgewezen: 'Afgewezen',
  gezien: 'Gezien',
  genegeerd: 'Geen bezwaar',
  teruggedraaid: 'Teruggedraaid',
  geen: 'Geen reactie'
};
function Step({
  title,
  last = false,
  children
}: {
  title: string;
  last?: boolean;
  children: React.ReactNode;
}) {
  return <li className="relative grid grid-cols-[1.25rem_minmax(0,1fr)] gap-4" data-id="element-192" data-mp="1m1b47kgav7a2">
      <span aria-hidden className="relative flex justify-center" data-id="element-193" data-mp="20xilabwevsl4">
        <span className="mt-1.5 h-2.5 w-2.5 rounded-full border border-accent/70 bg-night-900" data-id="element-194" data-mp="1vw308ju8hm2q" />
        {!last && <span className="absolute bottom-[-4px] top-5 w-px bg-white/10" data-id="element-195" data-mp="29vucaf16wse3" />}
      </span>
      <div className={last ? '' : 'pb-7'} data-id="element-196" data-mp="24ykmuzdswgq9">
        <h4 className="text-[13px] font-medium text-ink-muted" data-id="element-197" data-mp="24vq3lb6ix1yf">{title}</h4>
        <div className="mt-2" data-id="element-198" data-mp="2jldvjlsteo9h">{children}</div>
      </div>
    </li>;
}
export function WhyChain({
  event
}: {
  event: LogbookEvent;
}) {
  const {
    entry,
    chain,
    reconstructed
  } = event;
  const [rawOpen, setRawOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const raw = JSON.stringify({
    ...entry,
    chain
  }, null, 2);
  const status = STATUS[chain.execution.status];
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(raw);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      setCopied(false);
    }
  };
  return <article aria-labelledby="why-title" className="rounded-3xl border border-white/[0.07] bg-night-900 p-6 lg:p-8" data-id="element-199" data-mp="35luy08aonip6">
      <div className="flex items-start gap-4" data-id="element-200" data-mp="3afpez3ywljkk">
        <ActorMark actor={entry.agent} data-id="element-201" data-mp="3eeg2cjdkhmuh" />
        <div className="min-w-0" data-id="element-202" data-mp="3ehkbzw9sxasj">
          <p className="text-[13px] text-ink-muted" data-id="element-203" data-mp="3je1clpy6thmi">
            Waarom? · <span className="tabular-nums" data-id="element-204" data-mp="3jgnizm9cdr35">{entry.time}</span>
          </p>
          <h3 id="why-title" className="mt-1 text-[20px] font-semibold leading-snug text-ink" data-id="element-205" data-mp="3oe6xmn9yc5vm">
            {entry.summary}
          </h3>
          {reconstructed && <p className="mt-2 text-[13px] text-ink-muted" data-id="element-206" data-mp="1u0ed7yq6aw8u">Gereconstrueerd uit het spoor; niet elk detail is vastgelegd.</p>}
        </div>
      </div>

      <ol className="mt-8" data-id="element-207" data-mp="1z0k7lmntuwum">
        <Step title="Aanleiding" data-id="element-208" data-mp="g44ou0r0a5fb">
          <p className="text-[15px] text-ink" data-id="element-209" data-mp="g55z43076nvz">{chain.trigger.event}</p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px] text-ink-muted" data-id="element-210" data-mp="bcml54xllqqn">
            <code className="rounded-md bg-white/[0.05] px-2 py-0.5 font-mono text-[12px] text-ink-soft" data-id="element-211" data-mp="7cch3xtd9aef">{chain.trigger.topic}</code>
            <time className="font-mono text-[12px] tabular-nums" data-id="element-212" data-mp="2e15pinfaru3">{chain.trigger.timestamp}</time>
            <span data-id="element-213" data-mp="z1nyn6lafs2s">{chain.trigger.source}</span>
          </div>
        </Step>

        <Step title="Context" data-id="element-214" data-mp="v26ssc1y8sx3">
          <dl className="grid gap-x-6 gap-y-1.5 text-[14px] sm:grid-cols-[9rem_minmax(0,1fr)]" data-id="element-215" data-mp="uyraz1gw55rz">
            {chain.context.map((c, i) => <React.Fragment key={`${c.label}-${i}`} data-id="element-216" data-mp="l37i6nxmin7g" data-mpi={i}>
                <dt className="text-ink-muted" data-id="element-217" data-mp="l4sdifrria6d">{c.label}</dt>
                <dd className="text-ink-soft" data-id="element-218" data-mp="3at1bklfoctnq">{c.value}</dd>
              </React.Fragment>)}
          </dl>
        </Step>

        <Step title="Kandidaten · initiatiefscore" data-id="element-219" data-mp="35rluf4gmb7am">
          <ul className="space-y-2.5" data-id="element-220" data-mp="xjys0b9dacmk">
            {chain.candidates.map(c => <li key={c.action} data-id="element-221" data-mp="12ap19zkwyzmj">
                <div className="flex items-baseline justify-between gap-4 text-[14px]" data-id="element-222" data-mp="12bqkwsfzwufv">
                  <span className={c.chosen ? 'font-medium text-ink' : 'text-ink-soft'} data-id="element-223" data-mp="oklgld1ondz7">
                    {c.action}
                    {c.chosen && <span className="ml-2 text-[12px] font-medium text-accent" data-id="element-224" data-mp="tgiyhnslgse3">gekozen</span>}
                  </span>
                  <span className="tabular-nums text-ink-muted" data-id="element-225" data-mp="1h6d6ns1yxss0">{c.score.toFixed(2).replace('.', ',')}</span>
                </div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-white/[0.06]" data-id="element-226" data-mp="17atg7l66bman">
                  <div className={`h-full rounded-full ${c.chosen ? 'bg-accent' : 'bg-white/25'}`} style={{
                width: `${Math.max(2, c.score * 100)}%`
              }} data-id="element-227" data-mp="1c8lin94g2qt3" />
                </div>
                {c.note && <p className="mt-1 text-[12px] text-ink-muted" data-id="element-228" data-mp="ty4sgonc47d">{c.note}</p>}
              </li>)}
          </ul>
        </Step>

        <Step title="Besluit" data-id="element-229" data-mp="3pery45blk6r1">
          <div className="flex flex-wrap items-center gap-2" data-id="element-230" data-mp="3pe02iui0290r">
            <LevelBadge level={chain.decision.level} data-id="element-231" data-mp="3kfoqcd48fmfd" />
            <span className="text-[14px] text-ink-soft" data-id="element-232" data-mp="3kj2vft4a69qi">via {chain.decision.channel}</span>
          </div>
          <p className="mt-2 text-[14px] leading-relaxed text-ink-muted" data-id="element-233" data-mp="3thepy689pro8">{chain.decision.reason}</p>
        </Step>

        <Step title="Uitvoering via gateway" data-id="element-234" data-mp="35rc262bdz7vf">
          <p className="flex flex-wrap items-center gap-2 text-[14px] text-ink-soft" data-id="element-235" data-mp="35s3n8j7cxn7g">
            {chain.execution.gateway}
            <ArrowRightIcon className="h-3.5 w-3.5 text-ink-muted" aria-hidden data-id="element-236" data-mp="31offo0iq6rm7" />
            <code className="rounded-md bg-white/[0.05] px-2 py-0.5 font-mono text-[12px] text-ink-soft" data-id="element-237" data-mp="3fiyddynq56y6">{chain.execution.tool}</code>
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[13px]" data-id="element-238" data-mp="2n0uam12aiibn">
            <span className={`font-medium ${status.tone}`} data-id="element-239" data-mp="2i2j5u4quqy0a">{status.label}</span>
            <span className="tabular-nums text-ink-muted" data-id="element-240" data-mp="lz4lasnehugc">{formatLatency(chain.execution.latencyMs)}</span>
            <ProcessingTag where={chain.execution.where} data-id="element-241" data-mp="qy7os8nqfsm7" />
          </div>
          <p className="mt-1.5 text-[13px] text-ink-muted" data-id="element-242" data-mp="qw4s55s2r18i">{chain.execution.detail}</p>
        </Step>

        <Step title="Jouw reactie" last data-id="element-243" data-mp="vr0uayr0z4zc">
          <p className="text-[14px] text-ink" data-id="element-244" data-mp="10oiykbu3kw10">
            {REACTION[chain.reaction.kind]}
            {chain.reaction.at && <span className="ml-2 tabular-nums text-ink-muted" data-id="element-245" data-mp="31aeml9qvv14">{chain.reaction.at}</span>}
          </p>
          <p className="mt-1 text-[13px] text-ink-muted" data-id="element-246" data-mp="80erblvvoaw7">{chain.reaction.text}</p>
        </Step>
      </ol>

      <div className="mt-8 border-t border-white/[0.06] pt-4" data-id="element-247" data-mp="cxnanpsrikz1">
        <div className="flex items-center justify-between gap-3" data-id="element-248" data-mp="h0jjiosi32ps">
          <button type="button" aria-expanded={rawOpen} aria-controls="raw-json" onClick={() => setRawOpen(v => !v)} className="inline-flex items-center gap-2 whitespace-nowrap rounded-full px-1 py-1 text-[13px] font-medium text-ink-soft transition-colors duration-150 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-249" data-mp="gxxfv08qd7cu">
            <BracesIcon className="h-4 w-4" aria-hidden data-id="element-250" data-mp="344e1ix3i2ugg" />
            Ruwe JSON
            <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-snappy ${rawOpen ? 'rotate-180' : ''}`} aria-hidden data-id="element-251" data-mp="2z5ujv8vxw4tw" />
          </button>
          {rawOpen && <button type="button" onClick={copy} className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-1 text-[12px] text-ink-muted transition-colors duration-150 hover:text-ink" data-id="element-252" data-mp="2u4dizddggidb">
              {copied ? <CheckIcon className="h-3.5 w-3.5 text-accent" aria-hidden data-id="element-253" data-mp="2g8t9f8jm9cik" /> : <CopyIcon className="h-3.5 w-3.5" aria-hidden data-id="element-254" data-mp="jeyk2sq68i3h" />}
              {copied ? 'Gekopieerd' : 'Kopieer'}
            </button>}
        </div>
        <AnimatePresence initial={false} data-id="element-255" data-mp="edraco4gx8u6">
          {rawOpen && <motion.div id="raw-json" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.22,
          ease: [0.23, 1, 0.32, 1]
        }} className="overflow-hidden" data-id="element-256" data-mp="3jg5pe5as3759">
              <pre className="no-scrollbar mt-3 max-h-[420px] overflow-auto rounded-2xl bg-night-950/80 p-4 font-mono text-[12px] leading-5 text-ink-soft" data-id="element-257" data-mp="3emknw39z7v1b">
                {raw}
              </pre>
            </motion.div>}
        </AnimatePresence>
      </div>
    </article>;
}